import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, FormControl,
  InputLabel, MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import ProductService, { PRODUCT_CACHE_UPDATED_EVENT } from '../components/ProductService';
import { fetchProductOrderStatistics } from '../components/ProductStatisticsService';
import StoreService from '../components/StoreService';

const EMPTY_SUMMARY = { totalOrders: 0, totalQuantity: 0, totalRevenue: 0 };
const EMPTY_INVENTORY = { totalIn: 0, totalOut: 0, net: 0, movementCount: 0 };
const EMPTY_FILTERS = { start: '', end: '', product_type: '', store_id: '' };
const IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || 'https://office.le-celadon.ma';

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return String(value.$oid || value._id || value.id || '');
  return String(value);
};

const resolveImage = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  return `${IMAGE_BASE_URL.replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
};

const formatMoney = (value) => `${Number(value || 0).toLocaleString('fr-MA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} DH`;
const formatNumber = (value) => Number(value || 0).toLocaleString('fr-MA');

const formatDate = (order) => {
  const raw = order?.createdAt?.$date || order?.createdAt;
  if (raw) {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString('fr-MA');
  }
  return [order?.orderDate, order?.orderTime].filter(Boolean).join(' ');
};

const channelColor = (channel) => {
  const value = String(channel || '').toLowerCase();
  if (value === 'site') return 'info';
  if (value.includes('glovo')) return 'warning';
  if (value === 'kooul') return 'error';
  if (value === 'pos') return 'success';
  return 'default';
};

const SummaryCard = ({ label, value, hint, tone = 'default' }) => {
  const tones = {
    in: { background: '#edf9f0', border: '#8bd19d', value: '#146c2e' },
    out: { background: '#fff1ee', border: '#f3a08f', value: '#a52c1b' },
    net: { background: '#eef6ff', border: '#8bbcf0', value: '#155f9e' },
    default: { background: '#fff', border: 'divider', value: 'text.primary' },
  };
  const palette = tones[tone] || tones.default;
  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: palette.border, bgcolor: palette.background, borderRadius: 3, minHeight: 116 }}>
      <Typography variant="overline" color="text.secondary" fontWeight={800}>{label}</Typography>
      <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5, color: palette.value }}>{value}</Typography>
      {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
    </Paper>
  );
};

const ProductStatisticsPanel = ({ product, visible, stores }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [knownStores, setKnownStores] = useState([]);

  useEffect(() => {
    let active = true;
    const loadStatistics = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchProductOrderStatistics(normalizeId(product._id), {
          page: page + 1,
          limit: rowsPerPage,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
        });
        if (!active) return;
        setStatistics(data);
        setKnownStores((current) => {
          const merged = new Map(current.map((store) => [store.storeId, store]));
          (data?.byStore || []).forEach((store) => merged.set(store.storeId, store));
          return [...merged.values()];
        });
      } catch (loadError) {
        console.error('Unable to load product sales statistics', loadError);
        if (active) setError(loadError?.response?.data?.error || 'Impossible de charger les statistiques de ce produit.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadStatistics();
    return () => { active = false; };
  }, [product, page, rowsPerPage, filters]);

  const applyFilters = () => {
    setPage(0);
    setFilters(draftFilters);
  };
  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const summary = statistics?.summary || EMPTY_SUMMARY;
  const inventory = statistics?.inventory || EMPTY_INVENTORY;
  const orders = statistics?.orders || [];
  const storeOptions = [...new Map([
    ...(Array.isArray(stores) ? stores : []).map((store) => ({ storeId: normalizeId(store._id), storeName: store.name || store.storeCode })),
    ...knownStores,
  ].map((store) => [store.storeId, store])).values()];

  return (
    <Box role="tabpanel" hidden={!visible} sx={{ display: visible ? 'block' : 'none' }}>
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Avatar variant="rounded" src={resolveImage(product.url_product)} sx={{ width: 52, height: 52 }} />
            <Box>
              <Typography variant="h6" fontWeight={900}>{product.name}</Typography>
              <Typography variant="caption" color="text.secondary">{product.sku || product.barcode || normalizeId(product._id)}</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary">Les filtres s'appliquent aux ventes et aux mouvements d'inventaire.</Typography>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr)) auto' }, gap: 1.5 }}>
          <TextField type="date" label="Du" value={draftFilters.start} onChange={(event) => setDraftFilters((current) => ({ ...current, start: event.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField type="date" label="Au" value={draftFilters.end} onChange={(event) => setDraftFilters((current) => ({ ...current, end: event.target.value }))} InputLabelProps={{ shrink: true }} />
          <FormControl>
            <InputLabel>Canal</InputLabel>
            <Select label="Canal" value={draftFilters.product_type} onChange={(event) => setDraftFilters((current) => ({ ...current, product_type: event.target.value }))}>
              <MenuItem value="">Tous</MenuItem><MenuItem value="Site">WooCommerce</MenuItem><MenuItem value="Glovo">Glovo</MenuItem>
              <MenuItem value="Glovo_Sisters">Glovo Sisters</MenuItem><MenuItem value="Kooul">Kooul</MenuItem><MenuItem value="POS">POS</MenuItem><MenuItem value="Internal">Interne</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Magasin</InputLabel>
            <Select label="Magasin" value={draftFilters.store_id} onChange={(event) => setDraftFilters((current) => ({ ...current, store_id: event.target.value }))}>
              <MenuItem value="">Tous</MenuItem>
              {storeOptions.map((store) => <MenuItem key={store.storeId} value={store.storeId}>{store.storeName}</MenuItem>)}
            </Select>
          </FormControl>
          <Stack direction="row" gap={1}>
            <Button variant="contained" onClick={applyFilters} disabled={loading}>Filtrer</Button>
            <Button variant="text" onClick={resetFilters} disabled={loading}>Effacer</Button>
          </Stack>
        </Box>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
        <SummaryCard label="Commandes" value={summary.totalOrders || 0} hint="Références de vente" />
        <SummaryCard label="Quantité vendue" value={formatNumber(summary.totalQuantity)} hint="Lignes de commande" />
        <SummaryCard label="CA produit" value={formatMoney(summary.totalRevenue)} hint="Prix × quantité" />
        <SummaryCard label="Entrées inventaire" value={formatNumber(inventory.totalIn)} hint="Mouvements IN validés" tone="in" />
        <SummaryCard label="Sorties inventaire" value={formatNumber(inventory.totalOut)} hint="Mouvements OUT validés" tone="out" />
        <SummaryCard label="Solde des flux" value={formatNumber(inventory.net)} hint={`${formatNumber(inventory.movementCount)} mouvement(s)`} tone="net" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>Commandes par canal</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {(statistics?.byChannel || []).length ? statistics.byChannel.map((item) => (
              <Chip key={item.channel} color={channelColor(item.channel)} label={`${item.channel}: ${item.orders} commande(s) · ${formatNumber(item.quantity)} unité(s)`} />
            )) : <Typography variant="body2" color="text.secondary">Aucune donnée.</Typography>}
          </Stack>
        </Paper>
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>Commandes par magasin</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {(statistics?.byStore || []).length ? statistics.byStore.map((item) => (
              <Chip key={item.storeId} variant="outlined" label={`${item.storeName}: ${item.orders} · ${formatMoney(item.revenue)}`} />
            )) : <Typography variant="body2" color="text.secondary">Aucune donnée.</Typography>}
          </Stack>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading ? <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, bgcolor: 'rgba(255,255,255,.72)', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box> : null}
        <TableContainer>
          <Table sx={{ minWidth: 980 }}>
            <TableHead><TableRow><TableCell>Commande</TableCell><TableCell>Date</TableCell><TableCell>Canal</TableCell><TableCell>Magasin</TableCell><TableCell>Produit trouvé</TableCell><TableCell align="right">Qté</TableCell><TableCell align="right">CA produit</TableCell><TableCell>Statut</TableCell></TableRow></TableHead>
            <TableBody>
              {!loading && orders.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>Aucune commande liée à ce produit.</TableCell></TableRow>
              ) : orders.map((order) => (
                <TableRow key={normalizeId(order?._id)} hover>
                  <TableCell><Typography fontWeight={800}>#{order.orderNumber || order.orderCode}</Typography><Typography variant="caption" color="text.secondary">{order.CustomerName || order.orderCode}</Typography></TableCell>
                  <TableCell>{formatDate(order)}</TableCell>
                  <TableCell><Chip size="small" color={channelColor(order.productType)} label={order.productType || order.canal || 'Inconnu'} /></TableCell>
                  <TableCell>{order.storeName}</TableCell>
                  <TableCell>
                    {(order.matchedProducts || []).length ? order.matchedProducts.map((line, index) => (
                      <Box key={`${line.id_product || line.title}-${index}`} sx={{ mb: index < order.matchedProducts.length - 1 ? 0.75 : 0 }}>
                        <Typography variant="body2" fontWeight={700}>{line.title || product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{line.id_product || 'Référence liée à la commande'} · {line.matchSource === 'title' ? 'nom exact' : 'référence'}</Typography>
                      </Box>
                    )) : <Typography variant="body2">Référence liée à la commande</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{order.matchedQuantity || 0}</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{formatMoney(order.matchedRevenue)}</TableCell><TableCell><Chip size="small" variant="outlined" label={order.orderStatus || 'Inconnu'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={statistics?.pagination?.totalItems || 0} page={page} onPageChange={(_, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Lignes par page" />
      </Paper>
    </Box>
  );
};

const ProductSalesStatistics = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialProducts = useRef(Array.isArray(location.state?.products) ? location.state.products : []);
  const initialSelectedProductId = useRef(String(location.state?.selectedProductId || ''));
  const [products, setProducts] = useState(initialProducts.current);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(initialProducts.current.length === 0);
  const [stores, setStores] = useState([]);
  const [error, setError] = useState('');
  const [activeProductId, setActiveProductId] = useState('');
  const [loadedProductIds, setLoadedProductIds] = useState(() => new Set());
  const initializedFromUrl = useRef(false);

  useEffect(() => {
    let active = true;
    const applyProducts = (nextProducts) => {
      if (!active || !Array.isArray(nextProducts)) return;
      setProducts(nextProducts);
      setSelectedProducts((current) => current.map((selected) => nextProducts.find((product) => normalizeId(product._id) === normalizeId(selected._id)) || selected));
      setProductsLoading(false);
    };
    if (initialProducts.current.length === 0) {
      ProductService.fetchProducts().then(applyProducts).catch((loadError) => {
        console.error('Unable to load products for statistics', loadError);
        if (active) { setError('Impossible de charger la liste des produits.'); setProductsLoading(false); }
      });
    }
    StoreService.fetchStores().then((data) => {
      if (active && Array.isArray(data)) setStores(data.filter((store) => store?.is_active !== false));
    }).catch((loadError) => console.warn('Unable to load stores for product statistics', loadError));
    const handleCacheUpdate = (event) => applyProducts(event?.detail?.products);
    window.addEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleCacheUpdate);
    return () => { active = false; window.removeEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleCacheUpdate); };
  }, []);

  useEffect(() => {
    if (initializedFromUrl.current || products.length === 0) return;
    initializedFromUrl.current = true;
    const requestedIds = (
      searchParams.get('productIds')
      || searchParams.get('productId')
      || initialSelectedProductId.current
      || ''
    ).split(',').map((id) => id.trim()).filter(Boolean);
    const requestedProducts = requestedIds.map((id) => products.find((product) => normalizeId(product._id) === id)).filter(Boolean);
    if (requestedProducts.length) {
      const firstId = normalizeId(requestedProducts[0]._id);
      setSelectedProducts(requestedProducts); setActiveProductId(firstId); setLoadedProductIds(new Set([firstId]));
    }
  }, [products, searchParams]);

  const syncUrl = (nextProducts) => {
    const next = new URLSearchParams(searchParams);
    const ids = nextProducts.map((product) => normalizeId(product._id)).filter(Boolean);
    if (ids.length) { next.set('productIds', ids.join(',')); next.set('productId', ids[0]); }
    else { next.delete('productIds'); next.delete('productId'); }
    setSearchParams(next, { replace: true });
  };

  const handleProductsChange = (_, nextProducts) => {
    const unique = [...new Map(nextProducts.map((product) => [normalizeId(product._id), product])).values()];
    setSelectedProducts(unique);
    const selectedIds = new Set(unique.map((product) => normalizeId(product._id)));
    let nextActiveId = activeProductId;
    if (!selectedIds.has(nextActiveId)) nextActiveId = unique.length ? normalizeId(unique[unique.length - 1]._id) : '';
    if (unique.length > selectedProducts.length) nextActiveId = normalizeId(unique[unique.length - 1]._id);
    setActiveProductId(nextActiveId);
    setLoadedProductIds((current) => {
      const next = new Set([...current].filter((id) => selectedIds.has(id)));
      if (nextActiveId) next.add(nextActiveId);
      return next;
    });
    syncUrl(unique);
  };

  const handleTabChange = (_, productId) => {
    setActiveProductId(productId);
    setLoadedProductIds((current) => new Set([...current, productId]));
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ mb: 3 }}><Typography variant="h4" fontWeight={900}>Statistiques des ventes produit</Typography><Typography color="text.secondary">Sélectionnez plusieurs produits et analysez chacun dans son propre onglet.</Typography></Box>
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Autocomplete
          multiple limitTags={3} options={products} value={selectedProducts} loading={productsLoading} onChange={handleProductsChange}
          getOptionLabel={(product) => [product?.name, product?.sku || product?.barcode].filter(Boolean).join(' · ')}
          isOptionEqualToValue={(option, value) => normalizeId(option?._id) === normalizeId(value?._id)}
          renderOption={(props, product) => <Box component="li" {...props} key={normalizeId(product?._id)} sx={{ display: 'flex', gap: 1.5 }}><Avatar variant="rounded" src={resolveImage(product?.url_product)} /><Box><Typography fontWeight={700}>{product?.name}</Typography><Typography variant="caption" color="text.secondary">{product?.sku || product?.barcode || normalizeId(product?._id)}</Typography></Box></Box>}
          renderInput={(params) => <TextField {...params} label="Rechercher et sélectionner des produits" placeholder="Nom, SKU ou code-barres" InputProps={{ ...params.InputProps, endAdornment: <>{productsLoading ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</> }} />}
        />
      </Paper>
      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}
      {!selectedProducts.length ? <Alert severity="info">Choisissez un ou plusieurs produits pour afficher leurs statistiques.</Alert> : null}
      {selectedProducts.length ? (
        <>
          <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Tabs value={activeProductId} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
              {selectedProducts.map((product) => <Tab key={normalizeId(product._id)} value={normalizeId(product._id)} icon={<Avatar src={resolveImage(product.url_product)} sx={{ width: 28, height: 28 }} />} iconPosition="start" label={product.name} sx={{ minHeight: 62, textTransform: 'none', fontWeight: 800 }} />)}
            </Tabs>
          </Paper>
          {selectedProducts.map((product) => {
            const productId = normalizeId(product._id);
            if (!loadedProductIds.has(productId)) return null;
            return <ProductStatisticsPanel key={productId} product={product} visible={activeProductId === productId} stores={stores} />;
          })}
        </>
      ) : null}
    </Box>
  );
};

export default ProductSalesStatistics;
